import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, PenLine } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { haccpApi, erroreHaccp } from '../../haccpApi';
import { SignaturePad } from './SignaturePad';
import { PaginaFirma, useBlob } from './PaginaFirma';
import { DataConsegna } from './DataConsegna';

const CAMPO = 'mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm text-gray-900';
const parole = (s) => (s || '').toLowerCase().match(/[a-zà-ù]+/g) || [];
const stessa = (a, b) => parole(a).filter((p) => p.length > 2 && parole(b).includes(p)).length > 0;
const titolo = (s) => (s || '').toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
const RUOLI_MIEI = /titolar|datore|legale|responsabile|rspp|accettazione|cliente|richiedente|attuatore|azienda/i;

// Chi firma di solito: con la firma salvata conta il nome, senza si guarda il ruolo (titolare, datore, RSPP, soggetto attuatore...)
const sonoIo = (spazio, mia, nomeResp) => {
  if (spazio.manuale) return true;
  if (spazio.nome) return stessa(spazio.nome, mia?.nome || nomeResp) || (!mia && RUOLI_MIEI.test(spazio.ruolo));
  return RUOLI_MIEI.test(spazio.ruolo);
};
const nomeIniziale = (chiFirma, spazio, mia, nomeResp) => (chiFirma === 'io'
  ? (mia?.nome || (spazio?.nome ? titolo(spazio.nome) : nomeResp) || '') : titolo(spazio?.nome));
const simili = (doc, spazio) => (doc?.spazi || []).filter((x) => x.id !== spazio?.id && !x.firmata && !x.ignorata && x.box
  && (spazio?.nome ? stessa(x.nome, spazio.nome) : !x.nome && x.ruolo === spazio?.ruolo));

const Scelta = ({ chi, setChi }) => (
  <div className="grid grid-cols-2 gap-1 p-1 rounded-full border border-gray-300">
    {[['io', 'Firmo io'], ['altro', "Firma un'altra persona"]].map(([k, t]) => (
      <button key={k} type="button" onClick={() => setChi(k)} data-testid={`firma-chi-${k}`}
        className={`h-9 rounded-full text-xs font-semibold transition-colors ${chi === k ? 'bg-sky-700 text-white' : 'text-gray-700'}`}>{t}</button>
    ))}
  </div>
);

const ScegliPagina = ({ pagina, totale, setPagina }) => (totale !== 1 && (
  <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 p-1.5" data-testid="firma-scegli-pagina">
    <button type="button" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1} className="h-8 w-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-40" aria-label="Pagina prima" data-testid="firma-pagina-prima"><ChevronLeft className="h-4 w-4" /></button>
    <span className="text-xs font-semibold text-gray-800" data-testid="firma-pagina-numero">Pagina {pagina}{totale ? ` di ${totale}` : ''}</span>
    <button type="button" onClick={() => setPagina(pagina + 1)} disabled={!!totale && pagina >= totale} className="h-8 w-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-40" aria-label="Pagina dopo" data-testid="firma-pagina-dopo"><ChevronRight className="h-4 w-4" /></button>
  </div>
));

export const FirmaSpazio = ({ aperto, dati, onClose, onFirmato }) => {
  const { doc, spazio } = aperto || {};
  const mia = dati?.mia_firma;
  const miaUrl = useBlob(aperto && mia ? `/documenti/firme/mia?v=${mia.creata_il}` : null);
  const [chi, setChi] = useState('io');
  const [nome, setNome] = useState('');
  const [disegno, setDisegno] = useState(null);
  const [ridisegna, setRidisegna] = useState(false);
  const [box, setBox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tutti, setTutti] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [consegna, setConsegna] = useState('');
  const altri = simili(doc, spazio);

  useEffect(() => {
    if (!spazio) return;
    const c = sonoIo(spazio, mia, dati?.nome_responsabile) ? 'io' : 'altro';
    setChi(c);
    setNome(nomeIniziale(c, spazio, mia, dati?.nome_responsabile));
    setDisegno(null); setRidisegna(false); setBox(spazio.box); setTutti(true); setPagina(spazio.pagina); setConsegna('');
  }, [spazio]);
  useEffect(() => { setDisegno(null); setNome(nomeIniziale(chi, spazio, mia, dati?.nome_responsabile)); }, [chi]);

  const serveDisegno = chi === 'altro' || !mia || ridisegna;
  const anteprima = serveDisegno ? disegno : miaUrl;
  const firma = async () => {
    setBusy(true);
    try {
      const r = await haccpApi.post(`/documenti/firme/${doc.id}/applica`, {
        spazio_id: spazio.id, chi, box, pagina: spazio.manuale ? pagina : undefined, immagine: serveDisegno ? disegno : null, nome: serveDisegno ? nome : null,
        altri: tutti && !spazio.manuale ? altri.map((x) => x.id) : [], data_consegna: doc.chiede_data ? consegna : undefined });
      toast.success(r.firmati > 1 ? `Firmati ${r.firmati} spazi da ${r.firmatario}` : `Firmato da ${r.firmatario}`);
      onFirmato();
      onClose();
    } catch (e) { toast.error(erroreHaccp(e, 'Firma non applicata')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!aperto} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[96vw] max-h-[94vh] overflow-y-auto rounded-2xl" data-testid="firma-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6 flex items-center gap-2"><PenLine className="h-5 w-5 text-sky-700" />{spazio?.ruolo}</DialogTitle>
          <DialogDescription>{doc?.nome} · pagina {spazio?.manuale ? pagina : spazio?.pagina}{spazio?.nome ? ` · ${titolo(spazio.nome)}` : ''}</DialogDescription>
        </DialogHeader>
        {aperto && (
          <div className="space-y-3">
            {doc.chiede_data && <DataConsegna valore={consegna} onChange={setConsegna} />}
            <Scelta chi={chi} setChi={setChi} />
            {chi === 'altro' && <p className="text-xs text-gray-600">Passa il telefono alla persona: scrive il suo nome e firma qui sotto.</p>}
            {serveDisegno && (
              <label className="block text-xs text-gray-600">{chi === 'io' ? 'Il tuo nome e cognome' : 'Nome e cognome di chi firma'}
                <input value={nome} onChange={(e) => setNome(e.target.value)} className={CAMPO} style={{ background: '#fff' }} data-testid="firma-nome" />
              </label>
            )}
            {serveDisegno ? <SignaturePad key={`${chi}-${ridisegna}`} onChange={setDisegno} testid="firma-pad" /> : (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 p-2" data-testid="firma-mia-salvata">
                {miaUrl ? <img src={miaUrl} alt="La mia firma" className="h-12 max-w-[40%] object-contain rounded" style={{ background: '#fff' }} /> : <Loader2 className="h-4 w-4 animate-spin" />}
                <span className="text-xs text-gray-600 flex-1 min-w-0 truncate">La tua firma salvata ({mia.nome})</span>
                <button type="button" onClick={() => setRidisegna(true)} className="text-xs text-sky-700 font-medium" data-testid="firma-ridisegna">Ridisegna</button>
              </div>
            )}
            <p className="text-[11px] text-gray-500">{spazio.manuale ? 'Tocca la pagina nel punto dove vuoi la firma.' : 'Controlla dove va la firma: tocca la pagina per spostarla.'}</p>
            {spazio.manuale && <ScegliPagina pagina={pagina} totale={doc.pagine_totali} setPagina={setPagina} />}
            <PaginaFirma documentoId={doc.id} pagina={spazio.manuale ? pagina : spazio.pagina} box={box} onBox={setBox} anteprima={anteprima} />
            {altri.length > 0 && !spazio.manuale && (
              <label className="flex items-start gap-2 rounded-xl border border-sky-200 p-2.5 text-xs text-gray-700" data-testid="firma-anche-altri">
                <input type="checkbox" checked={tutti} onChange={(e) => setTutti(e.target.checked)} className="mt-0.5 h-4 w-4" data-testid="firma-anche-altri-check" />
                <span>Metti la stessa firma anche {altri.length === 1 ? "nell'altro spazio" : `negli altri ${altri.length} spazi`} {spazio.nome ? `di ${titolo(spazio.nome)}` : `"${spazio.ruolo}"`} di questo documento ({new Set(altri.map((x) => x.pagina)).size === 1 ? 'pagina' : 'pagine'} {[...new Set(altri.map((x) => x.pagina))].join(', ')}).</span>
              </label>
            )}
            <p className="text-[11px] text-amber-800 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="firma-avviso-definitiva">Firmando resta solo il documento firmato: quello senza firma si cancella. Controlla bene il punto prima di confermare.</p>
            <button onClick={firma} disabled={busy || (serveDisegno && (!disegno || nome.trim().length < 2)) || !box || (doc.chiede_data && !consegna)} data-testid="firma-conferma-btn"
              className="w-full h-12 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}Confermo e firmo
            </button>
            <p className="text-[11px] text-gray-500">Firma elettronica semplice: sul PDF compaiono la firma e la data di oggi.{doc.tipo === 'dvr' ? " Per il DVR la data certa la danno le firme sull'originale: tieni anche quello firmato." : ''}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
